import { useState } from "react";
import { WifiOff, Wifi, X } from "lucide-react";
import { useConnectionHealth } from "@/hooks/use-connection-health";
import {
  REALTIME_DISABLED_DISMISS_LABEL,
  REALTIME_DISABLED_NOTICE,
  dismissRealtimeNotice,
  realtimeNoticeDismissed,
} from "@/lib/realtime-notice";

/**
 * Top-of-app connection state. `degraded` and `offline` describe a configured
 * realtime client that is struggling, so they keep the alarm colours and the
 * "reconnecting" promise.
 *
 * `disabled` is a different fact: this build has no realtime client, the API
 * is fine and nothing is being retried. It gets one muted, dismissible line
 * and it must never say the server is unreachable — that message was on screen
 * permanently in production on 2026-09-11 while every request was succeeding.
 */
export function ConnectionBanner() {
  const status = useConnectionHealth();
  const [noticeDismissed, setNoticeDismissed] = useState(realtimeNoticeDismissed);

  if (status === "online") return null;

  if (status === "disabled") {
    if (noticeDismissed) return null;

    return (
      <div className="flex justify-center px-4 py-1">
        <span
          role="status"
          className="inline-flex items-center gap-2 rounded-full border border-app-border bg-app-surface-muted px-3 py-0.5 text-xs text-app-fg-muted"
        >
          {REALTIME_DISABLED_NOTICE}
          <button
            type="button"
            aria-label={REALTIME_DISABLED_DISMISS_LABEL}
            className="flex size-11 shrink-0 items-center justify-center rounded-full hover:text-app-fg-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand md:size-10"
            onClick={() => {
              dismissRealtimeNotice();
              setNoticeDismissed(true);
            }}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      </div>
    );
  }

  const config = {
    degraded: {
      bg: "bg-app-surface-muted text-app-warning",
      icon: Wifi,
      text: "Conexão instável. Algumas atualizações podem atrasar.",
    },
    offline: {
      bg: "bg-app-surface-muted text-app-danger",
      icon: WifiOff,
      text: "Atualizações em tempo real indisponíveis. Tentando reconectar...",
    },
  }[status];

  const Icon = config.icon;

  return (
    <div role="status" className={`${config.bg} border-b border-app-border px-4 py-1.5 text-xs flex items-center justify-center gap-2 z-50`}>
      <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden />
      <span>{config.text}</span>
    </div>
  );
}
